import {  reactive, not, Span, Div, Button, Checkbox  } from 'alkali'
import SelectivityDropdown from 'components/generic/SelectivityDropdown'
import Dialog from '../generic/DialogAlkali'
import Widget from '../generic/WidgetAlkali'
import { readonlyMode } from 'model/SearchQuery'
import './AppSettingsOptions.scss'
import { app, userSettings as appUserSettings } from '../../app/app'
import { analysisSelections } from '../../model/SearchQuery'
import { patch, put, get } from 'tools/request'
import { User } from 'types/dtos'


const DEFAULT_SETTINGS = {
	IsSI: false,
	FriendlyNames: false,
	ShowFilteredTerms: false,
	ShowUnbound: false,
	ShowVarianceType: false,
	TimepointUnit: 'None',
}

export default class Options extends Dialog {

	get defaults() {
		return {
			head: {
				titleText: 'Application Settings',
			},
			id: 'settings-dialog',
			loader: false,
			foot: {},

		}
	}

	ready(properties) {
		let settings = app.user.Settings
		const userSettings = reactive({
			AutoExpandRows: settings.AutoExpandRows,
			HideHighlights: settings.HideHighlights,
			HideErrorBars: settings.HideErrorBars,
			UnitStandardize: settings.IsSI,
			AutoRapidExtractValues: app.user.AutoRapidExtractValues,
			AutoAnalysisType: settings.AutoAnalysisType,
			DefaultAnalysisType: settings.DefaultAnalysisType || 'auto',
			NaturalQuestionParsing: settings.NaturalQuestionParsing,
			QuestionEditing: typeof settings.QuestionEditing == 'boolean' ? settings.QuestionEditing : app.user.IsInternal,
			MultiGroupPooling: settings.MultiGroupPooling ?? true,
		})
		this.children[1].style.display = 'table'
		if (readonlyMode.valueOf())
			this.body.append(makeReadonlyOptions(userSettings))
		else {
			this.body.append(
				makeQuestionEditingWidget(userSettings, properties.highlightQuestion),
				makeAutoAnalysisOption(userSettings),
				makeRapidExtractSettingsWidget(userSettings),
			)
		}

		this.footer.rightControls.append( Button('.btn.save-changes', {
			textContent: 'Save Changes',
			onclick: function(this) {
				let spinner = new Span('.spinner')
				this.textContent = ''
				this.append(spinner)
				settings.IsSI = userSettings.UnitStandardize.valueOf()
				app.user.AutoRapidExtractValues = userSettings.AutoRapidExtractValues.valueOf()
				let owner = analysisSelections.owner.valueOf()
				if ((!owner || owner == app.user.Id) && settings.AutoAnalysisType != userSettings.AutoAnalysisType)
					analysisSelections.settings.autoNMAType = userSettings.AutoAnalysisType.valueOf()
				settings.AutoAnalysisType = userSettings.AutoAnalysisType.valueOf()
				settings.DefaultAnalysisType = userSettings.DefaultAnalysisType.valueOf()
				settings.NaturalQuestionParsing = userSettings.NaturalQuestionParsing.valueOf()
				settings.QuestionEditing = userSettings.QuestionEditing.valueOf()
				settings.MultiGroupPooling = userSettings.MultiGroupPooling.valueOf()
				settings.AutoExpandRows = userSettings.AutoExpandRows.valueOf()
				settings.HideHighlights = userSettings.HideHighlights.valueOf()
				settings.HideErrorBars = userSettings.HideErrorBars.valueOf()

				return patch<User>('/portal/User/' + app.user.Id, {
					data: {
						Settings: settings,
						AutoRapidExtractValues: userSettings.AutoRapidExtractValues.valueOf()
					}
				}).then(data => {
					appUserSettings.updated()
					app.user.Settings.IsSI = userSettings.UnitStandardize.valueOf()
					this.textContent = ''
					let check = new Div('.success-checkmark', {},[
						Div('.check-icon', {}, [
							Span('.icon-line.line-tip', {},[]),
							Span('.icon-line.line-long', {},[]),
							Div('.icon-circle', {},[]),
							Div('.icon-fix', {},[]),
						])
					])
					this.append(check)
					setTimeout(function () {
						this.document.querySelector('.btn').textContent = `Save Changes`
						this.document.querySelector('.modal-alkali').hide()
					}, 1000)
				})
			}
		}))
	}
}
function makeReadonlyOptions(userSettings) {
	const widget = new Widget({
		head: {
			title: 'Analysis',
		},
	})
	widget.body.append(
		Div([
			Div('.user-settings', [
				Span(['Study List expanded to show outcome data']),
				Checkbox(userSettings.AutoExpandRows)
			]),
		]),
		Div([
			Div('.user-settings', [
				Span(['Highlight terms in title that match query']),
				Checkbox(not(userSettings.HideHighlights))
			]),
		]),
		Div([
			Div('.user-settings', [
				Span(['Show error bars/confidence intervals']),
				Checkbox(not(userSettings.HideErrorBars))
			]),
		]),
	)
	return widget
}

function makeAutoAnalysisOption(userSettings) {
	const widget = new Widget({
		head: {
			title: 'Analysis',
		},
	})
	widget.body.append(
		Div( [
			Div( '.user-settings', [
				Span(['Automatically pool study groups by default']),
				Checkbox(userSettings.MultiGroupPooling)
			]),
		]),
		Div( [
			Div( '.user-settings', [
				Span(['Study List expanded to show outcome data']),
				Checkbox(userSettings.AutoExpandRows)
			]),
		]),
		Div([
			Div( '.user-settings', [
				Span(['Highlight terms in title that match query']),
				Checkbox(not(userSettings.HideHighlights))
			]),
		]),
		Div([
			Div( '.user-settings', [
				Span(['Show error bars/confidence intervals']),
				Checkbox(not(userSettings.HideErrorBars))
			]),
		]),
		Div([
			Div('.user-settings', [
				Span(['Default Analysis Type']),
				SelectivityDropdown('.analysis-type-selector', {
					placeholder: 'Analysis Type',
					showSearchInputInDropdown: false,
					items: reactive(app.user)
						.whileResolving(null)
						.to((user) => {
							return [
								{ text: 'Automatic Analysis', id: 'auto' },
								{ text: 'Direct Meta Analysis', id: 'direct' },
								{ text: 'Bayesian NMA', id: 'bayesian' },
								{ text: 'Frequentist NMA', id: 'frequentist' },
								{ text: 'Cohort Analysis', id: 'cohort' },
								{
									text: 'Evidence Table Report',
									id: 'evidence-table',
								},
								{ text: 'Custom Analysis', id: 'custom', disabled: true },
							]
						}),
					value: userSettings.DefaultAnalysisType,
				}),
			], {
				title: 'This is the default analysis type that will be used whenever you ask a new question.',
			})
		]),
		Div([
			Div('.user-settings.double-button', [
				Span(['Automatic Analysis Type']),
				Button({
					float: 'right',
					textContent: 'Frequentist',
					onclick() {
						userSettings.AutoAnalysisType.put('frequentist')
					},
					classes: {
						selected: userSettings.AutoAnalysisType.to(data => data == 'frequentist' || !data)
					}
				}),
				Button({
					float: 'right',
					textContent: 'Bayesian',
					onclick() {
						userSettings.AutoAnalysisType.put('bayesian')
					},
					classes: {
						selected: userSettings.AutoAnalysisType.to(data => data == 'bayesian')
					},
				})
			])
		]),
		Div( '.user-settings.double-button'  , [
			Span(['Unit Standardization']),
			Button({
				float: 'right',
				textContent: 'SI',
				onclick() {
					userSettings.UnitStandardize.put(userSettings.UnitStandardize == true ? null : true)
				},
				classes: {
					selected: userSettings.UnitStandardize.to(data => data == true)
				},
		}),
			Button({
				textContent: 'Conventional',
				float: 'right',
				onclick() {
					userSettings.UnitStandardize.put(userSettings.UnitStandardize == false ? null : false)
				},
				classes: {
					selected: userSettings.UnitStandardize.to(data => data == false)
				},
			})
		])
	)
	return widget
}

function makeRapidExtractSettingsWidget(userSettings) {
	const widget = new Widget({
		head: {
			title: 'Rapid Extract',
		},
	})
	widget.body.append(
		Div([
			Div( '.user-settings'  , [
				Span(['Auto Suggest Rapid Extract Values (Beta)']),
				Checkbox(userSettings.AutoRapidExtractValues)
			]),
		])
	)
	return widget
}

function makeQuestionEditingWidget(userSettings, highlightQuestion) {
	const widget = new Widget(highlightQuestion ? '.highlighted-widget' : '', {
		head: {
			title: 'Search Bar',
		},
	})
	widget.body.append(
		Div( [
			Div( '.user-settings', [
				Span(['Ability to enter natural language question']),
				Checkbox(userSettings.QuestionEditing)
			]),
		]),
		Div([
			Div('.user-settings', [
				Span(['Enable experimental natural language question parsing']),
				Checkbox(userSettings.NaturalQuestionParsing)
			], {
				display: reactive(app.user).to(user => user.IsInternal),
				title: 'Enable new experimental natural language question parsing.',
			})
		]),
	)
	return widget
}
